"use client";
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import cn from "classnames";

import useWindowDimensions from "@/next-src/hooks/useWindowDimensions";
import ButtonLink from "@/next-src/components/ui/button-link/ButtonLink";
import convertHTMLtoJSX from "@/next-src/utils/convertHTMLtoJSX";
import getId from "@/lib/getId";
import ResponsiveImage from "@/next-src/components/ui/responsive-image/ResponsiveImage";
import Calendly from "@/next-src/components/ui/calendly/Calendly";

import comCSS from "./BasicSection.module.scss";

const BasicSection = ({ className = "", data = {} }) => {
  const idRef = useRef("");
  const { width: windowWidth } = useWindowDimensions();
  const [imgCSS, setImgCSS] = useState("");
  const {
    preTitleMobile,
    titleMobile,
    textMobile,
    buttonMobile,
    bgColorMobile,
    hideTextMobile,
    img,
    hideImg,
    isReversed,
    imgHeightDesktop,
    button,
    calendlyButtonSuccess,
    calendlyButtonSuccessMobile,
    paddingTopMobile,
    paddingBottomMobile,
  } = data || {};

  let { preTitle, title, text, bgColor, hideText, paddingTop, paddingBottom } =
    data || {};

  if (windowWidth < 992) {
    preTitle = preTitleMobile;
    title = titleMobile;
    text = textMobile;
    bgColor = bgColorMobile;
    hideText = hideTextMobile;
    paddingTop = paddingTopMobile;
    paddingBottom = paddingBottomMobile;
  }
  useEffect(() => {
    const id = getId();
    idRef.current = id;
    const css = `
      @media screen and (min-width: 992px) {
        .basic-section .img-wrapper-${id} {
          height: ${imgHeightDesktop}px;
        }
      }
    `;
    setImgCSS(css);
  }, [imgHeightDesktop]);

  const btnMR =
    buttonMobile.marginX[1] === 0
      ? "auto"
      : `${100 - buttonMobile.marginX[1]}%`;
  const btnML =
    buttonMobile.marginX[0] === 100 ? "auto" : `${buttonMobile.marginX[0]}%`;

  const btnMR_LG =
    button.marginX[1] === 0 ? "auto" : `${100 - button.marginX[1]}%`;
  const btnML_LG = button.marginX[0] === 100 ? "auto" : `${button.marginX[0]}%`;

  return (
    <section
      className={cn(
        className,
        comCSS.wrapper,
        "section background-color-soft-blue basic-section padding-section-large"
      )}
      style={{
        backgroundColor: bgColor,
        paddingTop:
          paddingTop !== null && paddingTop >= 0 ? `${paddingTop}px` : "",
        paddingBottom:
          paddingBottom !== null && paddingBottom >= 0
            ? `${paddingBottom}px`
            : "",
      }}
    >
      <style>{imgCSS}</style>
      <div className="padding-global">
        <div className="container-large">
          <div
            className={cn(comCSS.contentWrapper, {
              [comCSS.contentWrapperReversed]: isReversed,
              [comCSS.contentWrapperOneCol]: hideImg,
            })}
          >
            {!hideImg ? (
              <div
                offset={100}
                className={cn(
                  comCSS.imgWrapper,
                  `img-wrapper-${idRef.current}`,
                  {
                    [comCSS.imgWrapperVertical]: img?.height > img?.width,
                  }
                )}
              >
                <ResponsiveImage
                  src={img?.src}
                  alt={img?.alt}
                  sizes="(min-width: 992px) 490px, (min-width: 800px) 376px, calc( 100vw - 48px )"
                />
              </div>
            ) : null}

            <div className={cn(comCSS.content)}>
              <div className={cn(comCSS.contentInner, "max-width-large")}>
                <div className="margin-bottom margin-xsmall">
                  <div className="text-note">{convertHTMLtoJSX(preTitle)}</div>
                </div>
                <div className={cn("margin-bottom margin-xsmall")}>
                  {convertHTMLtoJSX(title)}
                </div>
                {!hideText ? (
                  <div
                    className={cn(
                      "margin-bottom margin-small",
                      comCSS.everyPlanDetailText
                    )}
                  >
                    {convertHTMLtoJSX(text)}
                  </div>
                ) : null}
                <div className={cn(comCSS.btnWrapper)}>
                  <div
                    className={cn(comCSS.btnWrapperInner)}
                    style={{
                      marginLeft: btnML_LG,
                      marginRight: btnMR_LG,
                    }}
                  >
                    {button.calendlyLink ? (
                      <Calendly
                        classNameBtn="is-secondary hide-tablet"
                        classNameBtnSuccess="is-secondary hide-tablet"
                        data={{
                          calendlyButton: button,
                          calendlyButtonMobile: button,
                          calendlyButtonSuccess,
                          calendlyButtonSuccessMobile,
                        }}
                      />
                    ) : (
                      <ButtonLink
                        data={button}
                        className="is-secondary hide-tablet"
                      />
                    )}
                  </div>
                </div>
                <div className={cn(comCSS.btnWrapper)}>
                  <div
                    className={cn(comCSS.btnWrapperInner)}
                    style={{
                      marginLeft: btnML,
                      marginRight: btnMR,
                    }}
                  >
                    {buttonMobile.calendlyLink ? (
                      <Calendly
                        classNameBtn="is-secondary hide-desktop"
                        classNameBtnSuccess="is-secondary hide-desktop"
                        data={{
                          calendlyButton: buttonMobile,
                          calendlyButtonMobile: buttonMobile,
                          calendlyButtonSuccess,
                          calendlyButtonSuccessMobile,
                        }}
                      />
                    ) : (
                      <ButtonLink
                        data={buttonMobile}
                        className="is-secondary hide-desktop"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

BasicSection.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    preTitle: PropTypes.string,
    preTitleMobile: PropTypes.string,
    title: PropTypes.string,
    titleMobile: PropTypes.string,
    text: PropTypes.string,
    hideText: PropTypes.bool,
    textMobile: PropTypes.string,
    hideTextMobile: PropTypes.bool,
    button: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    buttonMobile: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
      marginX: PropTypes.arrayOf(PropTypes.number), // Array of two numbers for horizontal margins
    }),
    calendlyButtonSuccess: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    calendlyButtonSuccessMobile: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    img: PropTypes.shape({
      id: PropTypes.string,
      src: PropTypes.string,
      alt: PropTypes.string,
    }),
    hideImg: PropTypes.bool,
    imgHeightDesktop: PropTypes.number,
    imgOriginalWidth: PropTypes.number,
    imgOriginalHeight: PropTypes.number,
    paddingTop: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    paddingBottom: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    paddingTopMobile: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    paddingBottomMobile: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    isReversed: PropTypes.bool,
    bgColor: PropTypes.string,
    bgColorMobile: PropTypes.string,
  }),
};

export default BasicSection;
